/**
 * @file 销售订单模块 - 常量定义
 * @description 定义销售订单模块使用的常量映射和配置
 * @对应后端 app/Modules/Sales/Models/SalesOrder.php
 * 
 * ⚠️ 注意事项：
 * 1. 常量名使用 UPPER_SNAKE_CASE
 * 2. 为每个枚举生成对应的标签映射
 * 3. 标签文本从后端 Model 的 get*TextAttribute() 方法提取
 * 4. 权限常量从 Seeder 文件提取
 * 
 * 📋 更新时机：
 * - 枚举添加新值
 * - 中文标签变更
 * - 权限定义变更
 * 
 * ✅ 验证方式：
 * 1. 对照枚举定义确保完整性
 * 2. 对照 Model 方法确保标签准确
 * 3. 确保所有映射关系正确
 * 
 * @last-updated 2025-10-19
 */

import { SalesOrderStatus, SalesOrderPriority } from './enums';

/**
 * 销售订单状态标签映射
 * @对应后端 SalesOrder::getStatusTextAttribute()
 */
export const SALES_ORDER_STATUS_LABELS: Record<SalesOrderStatus, string> = {
  [SalesOrderStatus.PENDING]: '待审核',
  [SalesOrderStatus.CONFIRMED]: '已确认',
  [SalesOrderStatus.IN_PRODUCTION]: '生产中',
  [SalesOrderStatus.COMPLETED]: '已完成',
  [SalesOrderStatus.CANCELLED]: '已取消',
};

/**
 * 销售订单优先级标签映射
 * @对应后端 SalesOrder::getPriorityTextAttribute()
 */
export const SALES_ORDER_PRIORITY_LABELS: Record<SalesOrderPriority, string> = {
  [SalesOrderPriority.URGENT]: '紧急',
  [SalesOrderPriority.HIGH]: '高',
  [SalesOrderPriority.MEDIUM]: '中',
  [SalesOrderPriority.LOW]: '低',
};

/**
 * 销售订单状态颜色映射（用于前端展示）
 */
export const SALES_ORDER_STATUS_COLORS: Record<SalesOrderStatus, string> = {
  [SalesOrderStatus.PENDING]: 'warning',
  [SalesOrderStatus.CONFIRMED]: 'processing',
  [SalesOrderStatus.IN_PRODUCTION]: 'processing',
  [SalesOrderStatus.COMPLETED]: 'success',
  [SalesOrderStatus.CANCELLED]: 'error',
};

/**
 * 销售订单优先级颜色映射（用于前端展示）
 */
export const SALES_ORDER_PRIORITY_COLORS: Record<SalesOrderPriority, string> = {
  [SalesOrderPriority.URGENT]: 'error',
  [SalesOrderPriority.HIGH]: 'warning',
  [SalesOrderPriority.MEDIUM]: 'processing',
  [SalesOrderPriority.LOW]: 'default',
};

/**
 * 默认币种
 */
export const DEFAULT_CURRENCY = 'CNY';

/**
 * ⚠️ DEFAULT_PAGE_SIZE 已移至 constants/default-config.ts
 * 
 * 请使用：
 * ```typescript
 * import { DEFAULT_PAGE_SIZE } from '@bayes-mes/shared/constants/default-config'
 * ```
 * 
 * @deprecated 请从 @bayes-mes/shared/constants/default-config 导入
 */
// export const DEFAULT_PAGE_SIZE = 20; - 已移至 constants/default-config.ts

/**
 * ⚠️ 销售订单权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * - PERM_SALES_ORDER_VIEW
 * - PERM_SALES_ORDER_CREATE
 * - PERM_SALES_ORDER_UPDATE
 * - PERM_SALES_ORDER_DELETE
 * - PERM_SALES_ORDER_APPROVE
 * 
 * ⚠️ 注意：以下权限后端暂未实现，请勿使用：
 * - sales:order:cancel
 * - sales:order:complete
 * - sales:order:export
 * - sales:order:import
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

